import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { 
  BarChart3, Download, TrendingUp, TrendingDown, Users, Eye, 
  Layers, Scale, ThumbsUp, ThumbsDown, RefreshCw, FileSpreadsheet,
  FileText, Calendar, Filter, ArrowUpRight, Clock, Globe, Building2,
  ChevronDown, ChevronUp, PieChart
} from 'lucide-react';

const B2BAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month');
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    materials: true,
    feedback: true,
    segments: true
  });

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/dashboard?period=${period}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Errore nel caricamento degli analytics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const exportExcel = async (reportType = 'overview') => {
    setExportingExcel(true);
    try {
      const response = await axios.get(
        `${API}/analytics/export/excel?period=${period}&report_type=${reportType}`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_${reportType}_${period}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Report Excel scaricato!');
    } catch (err) {
      toast.error('Errore nell\'export Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(
        `${API}/analytics/export/pdf?period=${period}`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_report_${period}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Report PDF scaricato!');
    } catch (err) {
      toast.error('Errore nell\'export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
        <p className="text-muted-foreground">Nessun dato analytics disponibile</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="b2b-analytics-dashboard">
      {/* Header Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Analytics B2B
          </h2>
          <p className="text-sm text-muted-foreground">
            Dati per vendita a produttori e clienti
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Period Selector */}
          <div className="flex items-center gap-2 bg-secondary rounded-lg p-1">
            {['week', 'month', 'quarter', 'year'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  period === p 
                    ? 'bg-white shadow-sm font-medium' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`period-${p}`}
              >
                {p === 'week' ? '7 giorni' : p === 'month' ? '30 giorni' : p === 'quarter' ? '90 giorni' : 'Anno'}
              </button>
            ))}
          </div>

          {/* Export Buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportExcel('overview')}
              disabled={exportingExcel}
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {exportingExcel ? 'Esportando...' : 'Excel'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportPdf}
              disabled={exportingPdf}
              data-testid="export-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              {exportingPdf ? 'Generando...' : 'PDF'}
            </Button>
          </div>

          <Button variant="ghost" size="sm" onClick={fetchDashboard}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <Eye className="w-4 h-4" />
            <span className="text-sm">Eventi Totali</span>
          </div>
          <p className="text-2xl font-bold">{formatNumber(data.overview?.total_events)}</p>
        </div>
        
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center gap-2 text-muted-foreground mb-2">
            <Users className="w-4 h-4" />
            <span className="text-sm">Utenti Unici</span>
          </div>
          <p className="text-2xl font-bold">{formatNumber(data.overview?.unique_users)}</p>
        </div>

        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <ThumbsUp className="w-4 h-4" />
            <span className="text-sm">Tasso Approvazione</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{data.conversions?.client_approval_rate || 0}%</p>
        </div>

        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center gap-2 text-[var(--gold)] mb-2">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">Conversione View→Board</span>
          </div>
          <p className="text-2xl font-bold text-[var(--gold)]">{data.conversions?.view_to_save_rate || 0}%</p>
        </div>
      </div>

      {/* Events Breakdown */}
      <div className="bg-white rounded-xl p-6 border shadow-sm">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <PieChart className="w-5 h-5" />
          Distribuzione Eventi
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {Object.entries(data.overview?.events_by_type || {}).slice(0, 12).map(([type, count]) => (
            <div key={type} className="bg-secondary/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground truncate" title={type}>
                {type.replace(/_/g, ' ')}
              </p>
              <p className="font-semibold">{formatNumber(count)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Material Performance */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('materials')}
          className="w-full p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
        >
          <h3 className="font-semibold flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Performance Materiali
            <span className="text-xs text-muted-foreground font-normal ml-2">
              (Dati per produttori)
            </span>
          </h3>
          {expandedSections.materials ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {expandedSections.materials && (
          <div className="p-4 pt-0 space-y-6">
            {/* Most Viewed */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Eye className="w-4 h-4" /> Materiali Più Visti
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-medium">Materiale</th>
                      <th className="text-left py-2 font-medium">Tipo</th>
                      <th className="text-left py-2 font-medium">Brand</th>
                      <th className="text-right py-2 font-medium">Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.materials?.most_viewed || []).slice(0, 10).map((mat, idx) => (
                      <tr key={mat._id || idx} className="border-b last:border-0 hover:bg-secondary/30">
                        <td className="py-2 font-medium">{mat.material_name || mat._id}</td>
                        <td className="py-2 text-muted-foreground">{mat.material_type || '-'}</td>
                        <td className="py-2 text-muted-foreground">{mat.material_brand || '-'}</td>
                        <td className="py-2 text-right font-semibold">{formatNumber(mat.views)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Comparison Pairs */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Scale className="w-4 h-4" /> Coppie Più Confrontate
              </h4>
              <div className="grid md:grid-cols-2 gap-3">
                {(data.materials?.comparison_pairs || []).slice(0, 6).map((pair, idx) => (
                  <div key={idx} className="bg-secondary/30 rounded-lg p-3 flex items-center justify-between">
                    <span className="text-sm">
                      {pair._id?.[0]?.substring(0, 20)} vs {pair._id?.[1]?.substring(0, 20)}
                    </span>
                    <span className="font-semibold">{pair.count}x</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Client Feedback Analysis */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('feedback')}
          className="w-full p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
        >
          <h3 className="font-semibold flex items-center gap-2">
            <ThumbsUp className="w-5 h-5" />
            Analisi Feedback Clienti
            <span className="text-xs text-muted-foreground font-normal ml-2">
              (Insight per produttori)
            </span>
          </h3>
          {expandedSections.feedback ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {expandedSections.feedback && (
          <div className="p-4 pt-0">
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <ThumbsUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-600">{formatNumber(data.client_feedback?.approved)}</p>
                <p className="text-sm text-green-700">Approvati</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <ThumbsDown className="w-6 h-6 text-red-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-600">{formatNumber(data.client_feedback?.rejected)}</p>
                <p className="text-sm text-red-700">Rifiutati</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <RefreshCw className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-yellow-600">{formatNumber(data.client_feedback?.alternatives)}</p>
                <p className="text-sm text-yellow-700">Alternative Richieste</p>
              </div>
            </div>

            {/* Most Rejected Materials - Important for manufacturers! */}
            <div>
              <h4 className="text-sm font-medium text-red-600 mb-3 flex items-center gap-2">
                <TrendingDown className="w-4 h-4" /> Materiali Più Rifiutati (Attenzione Produttori!)
              </h4>
              <div className="space-y-2">
                {(data.materials?.most_rejected || []).slice(0, 5).map((mat, idx) => (
                  <div key={mat._id || idx} className="flex items-center justify-between bg-red-50/50 rounded-lg p-3">
                    <div>
                      <p className="font-medium">{mat.material_name || mat._id}</p>
                      <p className="text-xs text-muted-foreground">{mat.material_type}</p>
                    </div>
                    <span className="text-red-600 font-semibold">{mat.rejections} rifiuti</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Segmentation */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('segments')}
          className="w-full p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
        >
          <h3 className="font-semibold flex items-center gap-2">
            <Users className="w-5 h-5" />
            Segmentazione Utenti
          </h3>
          {expandedSections.segments ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {expandedSections.segments && (
          <div className="p-4 pt-0 grid md:grid-cols-3 gap-6">
            {/* By Role */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Per Ruolo
              </h4>
              <div className="space-y-2">
                {Object.entries(data.segmentation?.by_user_role || {}).slice(0, 6).map(([role, count]) => (
                  <div key={role} className="flex items-center justify-between">
                    <span className="text-sm capitalize">{role?.replace(/_/g, ' ') || 'N/A'}</span>
                    <span className="font-semibold">{formatNumber(count)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* By Plan */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <ArrowUpRight className="w-4 h-4" /> Per Piano
              </h4>
              <div className="space-y-2">
                {Object.entries(data.segmentation?.by_subscription_plan || {}).map(([plan, count]) => (
                  <div key={plan} className="flex items-center justify-between">
                    <span className="text-sm capitalize">{plan || 'free'}</span>
                    <span className="font-semibold">{formatNumber(count)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* By Location */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4" /> Per Location
              </h4>
              <div className="space-y-2">
                {Object.entries(data.segmentation?.by_location || {}).slice(0, 6).map(([loc, count]) => (
                  <div key={loc} className="flex items-center justify-between">
                    <span className="text-sm">{loc}</span>
                    <span className="font-semibold">{formatNumber(count)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Time Series Chart Placeholder */}
      <div className="bg-white rounded-xl p-6 border shadow-sm">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Trend Temporale
        </h3>
        <div className="h-48 flex items-end gap-1">
          {(data.time_series || []).slice(-30).map((day, idx) => (
            <div 
              key={day._id || idx}
              className="flex-1 bg-primary/80 rounded-t hover:bg-primary transition-colors"
              style={{ 
                height: `${Math.max(10, (day.count / Math.max(...(data.time_series || []).map(d => d.count || 1))) * 100)}%`,
                minWidth: '4px'
              }}
              title={`${day._id}: ${day.count} eventi`}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-muted-foreground mt-2">
          <span>{data.time_series?.[0]?._id}</span>
          <span>{data.time_series?.[data.time_series.length - 1]?._id}</span>
        </div>
      </div>

      {/* Raw Data Export */}
      <div className="bg-secondary/30 rounded-xl p-6 text-center">
        <h3 className="font-semibold mb-2">Export Dati Grezzi</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Scarica i dati completi per analisi esterne o integrazione nei sistemi dei clienti
        </p>
        <div className="flex justify-center gap-3">
          <Button variant="outline" onClick={() => exportExcel('materials')}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Materiali
          </Button>
          <Button variant="outline" onClick={() => exportExcel('brands')}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Brand
          </Button>
          <Button variant="outline" onClick={() => exportExcel('feedback')}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Feedback
          </Button>
        </div>
      </div>
    </div>
  );
};

export default B2BAnalyticsDashboard;
